import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useToast } from "./";
import { expansionOrganizationalService } from "../services/ExpansionOrganizationalService";
import { NOTIFICATION_TYPES, parseMoneyValue } from "../utils";

export const useExpansionOrganizational = (accountId) => {
  const showToast = useToast();
  const [organizationalExpansions, setOrganizationalExpansions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track if we've already fetched for this accountId to prevent duplicate calls
  const lastFetchedAccountId = useRef(null);
  const isInitialized = useRef(false);

  // Memoize showToast to prevent infinite loops
  const stableShowToast = useMemo(() => showToast, [showToast]);

  const fetchOrganizationalExpansions = useCallback(async () => {
    if (!accountId) {
      setOrganizationalExpansions([]);
      return [];
    }

    console.log("Fetching organizational expansions for accountId:", accountId);
    setLoading(true);
    setError(null);

    try {
      const data =
        await expansionOrganizationalService.getOrganizationalExpansions(
          accountId,
        );
      const expansions = data.results || [];
      setOrganizationalExpansions(expansions);
      return expansions;
    } catch (error) {
      console.error("Error fetching organizational expansions:", error);
      setError(error);

      // Only show toast for actual errors, not for "no results" cases
      if (error.response?.status !== 404) {
        stableShowToast({
          content: "Failed to fetch organizational expansions",
          type: NOTIFICATION_TYPES.error,
        });
      }

      setOrganizationalExpansions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [accountId, stableShowToast]);

  const createOrganizationalExpansion = useCallback(
    async (expansionData) => {
      if (!accountId) return null;

      try {
        setLoading(true);
        const newExpansion =
          await expansionOrganizationalService.createOrganizationalExpansion(
            accountId,
            expansionData,
          );
        setOrganizationalExpansions((prev) => [...prev, newExpansion]);
        stableShowToast({
          content: "Organizational expansion created successfully",
          type: NOTIFICATION_TYPES.success,
        });
        return newExpansion;
      } catch (error) {
        console.error("Error creating organizational expansion:", error);
        stableShowToast({
          content: "Failed to create organizational expansion",
          type: NOTIFICATION_TYPES.error,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountId, stableShowToast],
  );

  const updateOrganizationalExpansion = useCallback(
    async (expansionId, expansionData) => {
      if (!accountId) return null;

      try {
        setLoading(true);
        const updatedExpansion =
          await expansionOrganizationalService.updateOrganizationalExpansion(
            accountId,
            expansionId,
            expansionData,
          );
        setOrganizationalExpansions((prev) =>
          prev.map((expansion) =>
            expansion.id === expansionId ? updatedExpansion : expansion,
          ),
        );
        stableShowToast({
          content: "Organizational expansion updated successfully",
          type: NOTIFICATION_TYPES.success,
        });
        return updatedExpansion;
      } catch (error) {
        console.error("Error updating organizational expansion:", error);
        stableShowToast({
          content: "Failed to update organizational expansion",
          type: NOTIFICATION_TYPES.error,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountId, stableShowToast],
  );

  const deleteOrganizationalExpansion = useCallback(
    async (expansionId) => {
      if (!accountId) return false;

      try {
        setLoading(true);
        await expansionOrganizationalService.deleteOrganizationalExpansion(
          accountId,
          expansionId,
        );
        setOrganizationalExpansions((prev) =>
          prev.filter((expansion) => expansion.id !== expansionId),
        );
        stableShowToast({
          content: "Organizational expansion deleted successfully",
          type: NOTIFICATION_TYPES.success,
        });
        return true;
      } catch (error) {
        console.error("Error deleting organizational expansion:", error);
        stableShowToast({
          content: "Failed to delete organizational expansion",
          type: NOTIFICATION_TYPES.error,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountId, stableShowToast],
  );

  // Calculate summary statistics - memoized to avoid recalculation
  const getSummaryStats = useCallback(() => {
    const totalOpportunities = organizationalExpansions.length;
    const totalPotentialMRR = organizationalExpansions.reduce(
      (sum, expansion) => {
        return sum + parseMoneyValue(expansion.additional_mrr).value;
      },
      0,
    );

    return {
      totalOpportunities,
      totalPotentialMRR,
      expansions: organizationalExpansions,
    };
  }, [organizationalExpansions]);

  // Only fetch once when accountId changes
  useEffect(() => {
    // Skip if no accountId
    if (!accountId) {
      setOrganizationalExpansions([]);
      setLoading(false);
      setError(null);
      lastFetchedAccountId.current = null;
      isInitialized.current = false;
      return;
    }

    // Skip if already fetched for this accountId
    if (lastFetchedAccountId.current === accountId && isInitialized.current) {
      console.log("Skipping fetch - already loaded for accountId:", accountId);
      return;
    }

    console.log("Initial fetch for accountId:", accountId);
    lastFetchedAccountId.current = accountId;
    isInitialized.current = true;

    let isCancelled = false;

    const loadData = async () => {
      try {
        if (!isCancelled) {
          setLoading(true);
          setError(null);
        }

        const data =
          await expansionOrganizationalService.getOrganizationalExpansions(
            accountId,
          );
        const expansions = data.results || [];

        if (!isCancelled) {
          setOrganizationalExpansions(expansions);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching organizational expansions:", error);
          setError(error);

          // Only show toast for actual errors, not for "no results" cases
          if (error.response?.status !== 404) {
            stableShowToast({
              content: "Failed to fetch organizational expansions",
              type: NOTIFICATION_TYPES.error,
            });
          }

          setOrganizationalExpansions([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      console.log("Cleanup for accountId:", accountId);
      isCancelled = true;
      // Reset loading state when component unmounts or accountId changes
      setLoading(false);
    };
  }, [accountId, stableShowToast]);

  return {
    organizationalExpansions,
    loading,
    error,
    fetchOrganizationalExpansions,
    createOrganizationalExpansion,
    updateOrganizationalExpansion,
    deleteOrganizationalExpansion,
    getSummaryStats,
  };
};
